import React, { useEffect, ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploadWithPreview from './ImageUploadWithPreview';
import LogoImage from '../../assets/images/mpower-logo.png';
import './UploadCards.scss';
import Button from '@material-ui/core/Button';
import { useState } from 'react';
import AuthUtils from '../../utils/AuthUtils';
import NavRoutes from '../../constants/NavRoutes';
import { PATIENT_BASE_ROUTE } from '../../constants/BaseRoute';
import { _logout, _patientLogout, _snackbarAcknowledgement, _uploadedDocuments } from '../../../../shared/src/store/actions/AuthActions';
import { useDispatch, useSelector } from 'react-redux';
import { DialogActions, DialogContent, Dialog, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router';
import { getDocumentsRequired, uploadDocuments } from '../../../../shared/src/services/PatientDocumentUpload';
import { getDocumentsId, getDocuments } from '../../../../shared/src/services/PatientService';
import AppLoader from '../../components/apploader/AppLoader';
import { Document } from '../../../../shared/src/services/service-mappers/BaseMapper';
const useStyles = makeStyles(() => ({
  root: {
    '& .MuiToolbar-regular': {
      minHeight: 0
    },
    '& .MuiOutlinedInput-root': {
      border: 0
    }
  },
  button: {
    borderRadius: 8,
    width: '5.1vw',
    height: '65%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    textTransform: 'none',
    float: 'right'
  },
  saveButton: {
    borderRadius: 8,
    width: '6.1vw',
    height: '65%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    textTransform: 'none',
    float: 'right',
  },
  dialog: {
    '& .MuiDialog-paperScrollPaper': {
      borderRadius: 8,
      boxShadow: '0 10px 30px 0 rgba(0, 0, 0, 0.05)'
    }
  }
}));

interface CardType {
  id: number;
  label: string;
  name_id: string;
  is_front_enabled: boolean;
  is_back_enabled: boolean;
  is_front_required: boolean;
  is_back_required: boolean;
  types_allowed: string[]
}

function formatList(items: string[], symbol: "&" | "and"): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${symbol} ${items[1]}`;
  const allButLast = items.slice(0, -1).join(', ');
  const lastItem = items[items.length - 1];
  return `${allButLast} ${symbol} ${lastItem}`;
}

const UploadCards = (props: any): JSX.Element => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const newFilesCount = useRef(0);
  const newReqFilesCount = useRef(0);
  const [showLoader, setShowLoader] = useState({
    fetchRequiredDocs: true,
    fetchUploadedDocs: true,
    submitFiles: false,
  })
  const [Cards, setCards] = useState<CardType[]>([]);
  const [previouslyUploadedCards, setPreviouslyUploadedCards] = useState<any[][]>([]);
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const details = useSelector((state: any) => state.auth)
  const unblock = useRef<any>();
  const patientId = details.patientId
  const [headLine, setHeadLine] = useState<string[]>([]);
  const handleFileSelect = (file: File | null, card: boolean, name: string, isReplaced: boolean) => {
    if (file) {
      if (!isReplaced) {
        newFilesCount.current = newFilesCount.current + 1;
        newReqFilesCount.current = card ? newReqFilesCount.current + 1 : newReqFilesCount.current;
      }
      setUploadedFiles((prevFiles) => ({ ...prevFiles, [name]: file }));
    }
    else if (file === null) {
      newFilesCount.current = newFilesCount.current - 1;
      newReqFilesCount.current = card ? newReqFilesCount.current - 1 : newReqFilesCount.current;
      setUploadedFiles((prevFiles) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [name]: _, ...restFiles } = prevFiles; // Remove the specific file using destructuring
        return restFiles;
      });
    }
  };
  const getRequiredDocuments = async () => {
    try {
      const response = await getDocumentsRequired();
      const tempArray = []
      setCards(response.data);
      for(const item of response.data) {
        tempArray.push(item.label)
      }
      setHeadLine(tempArray);
    }
    catch (error) {
      const err = error as any;
      if (err) {
        if (err.response && err.response.status === 400) {
          dispatch(_snackbarAcknowledgement(true, err.response.data.error.message, 'error'))
        } else {
          dispatch(_snackbarAcknowledgement(true, err.message, 'error'))
        }
      }
    }
    finally {
      setShowLoader((previous) => ({ ...previous, fetchRequiredDocs: false }))
    }
  }

  const getPatientDocuments = async () => {
    try {
      let allDocuments: Document[] = [];

      // eslint-disable-next-line prefer-const
      let documentIdsResponse = await getDocumentsId(patientId);

      const documentIds: number[] = documentIdsResponse.data;
      const documentPromises: Promise<any>[] = documentIds.map(id => getDocuments(patientId, id));

      const responses = await Promise.all(documentPromises);
      responses.forEach(response => {
        if (response && response.data) {
          allDocuments = allDocuments.concat(response.data);
        }
      });
      // Group documents by label
      const cardGroupsMap = new Map<string, any[]>();
      allDocuments.forEach((doc) => {
        const key = doc.label;
        if (!cardGroupsMap.has(key)) {
          cardGroupsMap.set(key, []);
        }
        cardGroupsMap.get(key)?.push(doc);
      });
      // Convert Map values to an array
      const AllCards = Array.from(cardGroupsMap.values());
      // Sort each subarray so that 'is_front: true' comes first
      AllCards.forEach((subarray) => {
        subarray.sort((a, b) => (a.is_front === true ? -1 : 1) - (b.is_front === true ? -1 : 1));
      });
      // Sort `AllCards` by length (more items in a subarray should come first)
      AllCards.sort((a, b) => b.length - a.length);
      setPreviouslyUploadedCards(AllCards);
    }
    catch (error) {
      const err = error as any;
      if (err) {
        if (err.response && err.response.status === 400) {
          dispatch(_snackbarAcknowledgement(true, err.response.data.error.message, 'error'))
        } else {
          dispatch(_snackbarAcknowledgement(true, err.message, 'error'))
        }
      }
    }
    finally {
      setShowLoader((previous) => ({ ...previous, fetchUploadedDocs: false }))
    }
  }
  useEffect(() => {
    patientId !== undefined && getPatientDocuments();
    patientId !== undefined && getRequiredDocuments();
  }, [patientId]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let requiredFilesCount = 0;
    let availableFilesCount = newReqFilesCount.current;
    Cards.forEach(card => {
      if (card.is_front_required) {
        requiredFilesCount++;
        const previouslyUploadedFront = previouslyUploadedCards.some(
          group => group.some(item => item.is_front && item.label === card.label)
        );
        if (previouslyUploadedFront) {
          availableFilesCount++;
        }
      }

      if (card.is_back_required) {
        requiredFilesCount++;
        const previouslyUploadedBack = previouslyUploadedCards.some(
          group => group.some(item => item.is_back && item.label === card.label)
        );
        if (previouslyUploadedBack) {
          availableFilesCount++;
        }
      }
    });
    setSubmitEnabled(availableFilesCount >= requiredFilesCount && newFilesCount.current > 0);
  }, [Cards, previouslyUploadedCards, newFilesCount.current]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    AuthUtils.clearPatientAuthToken();
    dispatch(_logout());
    dispatch(_patientLogout());
    setShowConfirmation(false);
    if (unblock.current) {
      unblock.current()
    }
    history.push(`/${PATIENT_BASE_ROUTE}/${NavRoutes.PUBLIC[404]}`);
  };

  const handleCancelBack = () => {
    setShowConfirmation(false);
  };

  useEffect(() => {
    if (unblock.current) {
      unblock.current()
    }
    unblock.current = history.block((location: any) => {
      setShowConfirmation(true);
    });

    return () => {
      if (unblock.current) {
        unblock.current()
      }
    };
  }, [history, newReqFilesCount.current]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      AuthUtils.clearPatientAuthToken();
      dispatch(_logout());
      dispatch(_patientLogout());
      history.push(`/${PATIENT_BASE_ROUTE}/${NavRoutes.PUBLIC[404]}`);
    };
    window.addEventListener('unload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('unload', beforeUnloadHandler);
    };
  }, [newReqFilesCount.current, history, dispatch]);// eslint-disable-line react-hooks/exhaustive-deps

  const renderBackDialog = (): ReactElement => {
    return (
      <Dialog
        open={showConfirmation}
        maxWidth={'md'}
        className={classes.dialog}
      >
        <DialogContent>
          <div className='form-container'>
            <div className="title-container">
              {t('PATIENT_DOCUMENT_UPLOAD.LEAVE')}
            </div>
            <div className='text-field-container'>
              {t('PATIENT_DOCUMENT_UPLOAD.CONFIRMATION_TEXT')}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="actions-container">
          <Button
            variant="contained"
            className={[classes.saveButton, 'yes-button'].join(' ')}
            onClick={handleBack}
          >
            <span className="button-text">
              {t('ANESTHETICINFUSION.YESRADIOBUTTONTEXT')}
            </span>
          </Button>
          <Button
            variant="contained"
            className={[classes.saveButton].join(' ')}
            onClick={handleCancelBack}
          >
            <span className="button-text">
              {t('ANESTHETICINFUSION.NORADIOBUTTONTEXT')}
            </span>
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const handleSubmit = async () => {
    try {
      const uploadedLabels = Cards.filter((card) => {
        const frontKey = `front_${card.id}`;
        const backKey = `back_${card.id}`;
        return (
          (card.is_front_enabled && uploadedFiles[frontKey]) ||
          (card.is_back_enabled && uploadedFiles[backKey])
        );
      }).map((card) => card.label);

      dispatch(_uploadedDocuments(uploadedLabels));

      setShowLoader({ ...showLoader, submitFiles: true });

      const data = new FormData();
      Object.keys(uploadedFiles).forEach((key) => {
        data.append(key, uploadedFiles[key]);
      });

      let response;
      let timeoutErrorOccurred = false;

      try {
        response = await uploadDocuments(data);
      } catch (error: any) {
        if (error.code === "ECONNABORTED") {
          timeoutErrorOccurred = true;
        } else {
          throw error; // If it's not a timeout, throw the error
        }
      }

      if (timeoutErrorOccurred) {
        // Retry bulk upload once
        response = await uploadDocuments(data);
      }

      setShowLoader({ ...showLoader, submitFiles: false });

      if (response?.statusType === "Success") {
        dispatch(
          _snackbarAcknowledgement(
            true,
            t("PATIENT_DOCUMENT_UPLOAD.SUBMIT_SUCCESS"),
            t("COMMON.SUCCESS")
          )
        );
      }

      // Reset state after success
      setUploadedFiles({});
      newFilesCount.current = 0;
      newReqFilesCount.current = 0;
      AuthUtils.clearPatientAuthToken();
      dispatch(_logout());
      dispatch(_patientLogout());

      if (unblock.current) {
        unblock.current();
      }

      history.push(
        `/${PATIENT_BASE_ROUTE}/${NavRoutes.PRIVATE.PATIENT.DOCUMENTS}/${NavRoutes.PRIVATE.PATIENT.SUCCESS}`
      );
    } catch (error: any) {
      setShowLoader({ ...showLoader, submitFiles: false });

      dispatch(
        _snackbarAcknowledgement(
          true,
          t("PATIENT_PORTAL.UPLOAD_FAILED_MESSAGE"),
          t("COMMON.ERRORTEXT")
        )
      );
      throw error;
    }
  };

  return (showLoader.fetchRequiredDocs || showLoader.fetchUploadedDocs || showLoader.submitFiles) ? (<AppLoader />) : (
    <div className="upload-cards-wrapper">
      <div className="upload-logo-wrapper">
        <img src={LogoImage} />
      </div>
      <div>
        <p className="title">
          {t('COMMON.SUBMIT')} {t('COMMON.YOUR')} <span className="card-type">{formatList(headLine, "&")}</span>
        </p>
        <p className="info">
          {t('PATIENT_DOCUMENT_UPLOAD.PATIENT_INFO', {
            firstName: details.firstName,
            lastName: details.lastName,
            surgeon: details.surgeon,
            dos: details.dos,
            cards: formatList(headLine, "and")
          })}
        </p>
      </div>

      <div className="upload-cards-items-wrapper">
        {Cards.map(card => {
          const isCardOptional = !card.is_front_required && !card.is_back_required;

          // Get previously uploaded images for the current card
          const previouslyUploaded = previouslyUploadedCards.find(
            (uploadedGroup) => uploadedGroup.some((item) => item.label === card.label)
          );

          const frontImageData = previouslyUploaded?.find((item) => {
            if (item.is_front) {
              return true; // Return true to find this item
            }
            return false; // Otherwise, continue searching
          })?.file_content?.data;

          const frontImageMimeType = previouslyUploaded?.find((item) => {
            if (item.is_front) {
              return true; // Return true to find this item
            }
            return false; // Otherwise, continue searching
          })?.mime_type

          const backImageData = previouslyUploaded?.find((item) => {
            if (item.is_back) {
              return true; // Return true to find this item
            }
            return false; // Otherwise, continue searching
          })?.file_content?.data;

          const backImageMimeType = previouslyUploaded?.find((item) => {
            if (item.is_back) {
              return true; // Return true to find this item
            }
            return false; // Otherwise, continue searching
          })?.mime_type;

          const createImageURL = (file: File | undefined, data: number[] | undefined, mimeType: string | undefined) =>
            file
              ? URL.createObjectURL(file) // Use the newly uploaded file first
              : data && mimeType
                ? URL.createObjectURL(new Blob([new Uint8Array(data)], { type: mimeType })) // Fallback to previously uploaded
                : null;

          const previouslyExists = (data: number[] | undefined, mimeType: string | undefined) =>
            !!(data && mimeType);

          return (
            <div key={card.label} className="upload-card-wrapper">
              <p className="upload-card-title">
                <span className="card-name">{t('COMMON.ADD')} {card.label}</span>{' '}
                {isCardOptional && <>({t('COMMON.OPTIONAL')})</>}
              </p>
              <div className="upload-card-input-wrapper">
                {card.is_front_enabled ? (
                  <ImageUploadWithPreview
                    placeholder="Add front picture of card"
                    onFileSelect={(file, isReplaced) => handleFileSelect(file, card.is_front_required, `front_${card.id}`, isReplaced)}
                    typesAllowed={card.types_allowed}
                    isRequired={card.is_front_required}
                    defaultImage={createImageURL(uploadedFiles[`front_${card.id}`], frontImageData, frontImageMimeType)}
                    previouslyExits={previouslyExists(frontImageData, frontImageMimeType)}
                  />
                ) : null}
                {card.is_back_enabled ? (
                  <ImageUploadWithPreview placeholder="Add back picture of card"
                    onFileSelect={(file, isReplaced) => handleFileSelect(file, card.is_back_required, `back_${card.id}`, isReplaced)}
                    typesAllowed={card.types_allowed}
                    isRequired={card.is_back_required}
                    defaultImage={createImageURL(uploadedFiles[`back_${card.id}`], backImageData, backImageMimeType)}
                    previouslyExits={previouslyExists(backImageData, backImageMimeType)}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <Button
        className="upload-submit-button"
        variant="contained"
        color="primary"
        disableRipple
        disabled={!submitEnabled || showLoader.submitFiles}
        onClick={handleSubmit}>
        {t('COMMON.SUBMIT')}
      </Button>
      {showConfirmation && renderBackDialog()}
    </div>
  );
};

export default UploadCards;